import { EntryType, ReservedNodeType } from '@morph-mapper/types';
import type { EntryItemType, TreeNode } from '@morph-mapper/types';
import { Plugin, resetProgress, usePlugins } from '@morph-mapper/plugins';
import { useStore } from '@/store';
import type { StoredSuggestion } from '@/store';
import { recomputeDependencies } from '../utils/dependencies';
import { replacementFor } from '../utils/suggestions';
import { useSuggestionRunner } from './use-suggestion-runner';

/**
 * Runs the plugins again on the current input and template (#1071):
 * a forced bootstrap, then a fresh evaluation of the selected fields.
 * Empty fields get their best suggestion filled in. A value that came from
 * a suggestion is replaced when a better one ends up on top; a value typed
 * by hand is never overwritten, it only gets new suggestions. The Iterator
 * plugin only runs when an iterator field is selected, and an empty
 * iterator gets the iterator it detected.
 */
export const useReevaluate = () => {
  const { bootstrap } = usePlugins();
  const { run } = useSuggestionRunner();
  const [
    getEntries,
    hasValue,
    getType,
    setType,
    getTemplateValue,
    setTemplateValue,
  ] = useStore(({ entries: e }) => [
    e.getEntries,
    e.hasValue,
    e.getType,
    e.setType,
    e.getTemplateValue,
    e.setTemplateValue,
  ]);
  const getTextContent = useStore(({ config: c }) => c.getTextContent);
  const [
    setRebootstrapInProgress,
    completeRebootstrap,
    setFieldStatus,
    markSuggestionApplied,
  ] = useStore(({ ai }) => [
    ai.setRebootstrapInProgress,
    ai.completeRebootstrap,
    ai.setFieldStatus,
    ai.markSuggestionApplied,
  ]);

  const applySuggestion = (entryId: string, suggestion: StoredSuggestion) => {
    if (getType(entryId) !== suggestion.type) {
      setType(entryId, suggestion.type as unknown as EntryItemType);
    }
    setTemplateValue(
      entryId,
      suggestion.type === EntryType.Code
        ? suggestion.snippet
        : suggestion.value,
    );
    markSuggestionApplied(entryId, suggestion);
  };

  /** Fills an empty field, or replaces a value that came from a suggestion. */
  const applyBest = (entryId: string, suggestions: StoredSuggestion[]) => {
    const best = hasValue(entryId)
      ? replacementFor(suggestions, {
          type: getType(entryId),
          value: getTemplateValue(entryId),
        })
      : suggestions.find((s) => s.autofill === true);
    if (best) applySuggestion(entryId, best);
  };

  /** Evaluates fields without a new bootstrap, then applies the best. */
  const evaluateAndFill = async (fieldIds: string[]) => {
    await run(fieldIds, { markNewest: true });
    const { suggestions } = useStore.getState().ai;
    fieldIds.forEach((id) => applyBest(id, suggestions[id] ?? []));
  };

  /** Bootstraps on a copy of the entries and returns that copy. */
  const rebootstrap = async (withIterators: boolean) => {
    setRebootstrapInProgress(true);
    resetProgress();
    try {
      const local: Record<string, TreeNode> = JSON.parse(
        JSON.stringify(getEntries()),
      );
      await bootstrap({
        entries: local,
        textContent: getTextContent() ?? '',
        recomputeDependencies,
        force: true,
        exclude: withIterators ? [] : [Plugin.Iterator],
      });
      return local;
    } finally {
      completeRebootstrap();
    }
  };

  /** Gives empty iterators the value the Iterator plugin detected. */
  const adoptDetectedIterator = (
    entryId: string,
    detected: TreeNode | undefined,
  ) => {
    const value = (detected as { value?: unknown } | undefined)?.value;
    if (!detected || hasValue(entryId) || value === undefined || value === '')
      return;
    if (getType(entryId) !== detected.type) {
      setType(entryId, detected.type as EntryItemType);
    }
    setTemplateValue(entryId, value);
  };

  const reevaluate = async (fieldIds: string[]) => {
    const entries = getEntries();
    const iteratorIds = fieldIds.filter(
      (id) => String(entries[id]?.key) === ReservedNodeType.Iterator,
    );

    fieldIds.forEach((id) => setFieldStatus(id, 'queued'));
    const local = await rebootstrap(iteratorIds.length > 0);
    // Before the other fields, so they are evaluated below the new iterator.
    iteratorIds.forEach((id) => adoptDetectedIterator(id, local[id]));
    await evaluateAndFill(fieldIds);
  };

  return { reevaluate, evaluateAndFill };
};
